// Cookie utility helpers for app/browser identity and preference persistence.
// Mistake note: relying only on crypto.randomUUID broke in older webviews; always add a compatibility fallback.
import cookie from "react-cookies";

const COOKIE_PREFIX = process.env.REACT_APP_COOKIE_PREFIX;
const COOKIE_INSTANCE_PREFIX = process.env.REACT_APP_COOKIE_INSTANCE_PREFIX;

const createUuidV4 = (): string => {
  const cryptoApi = window.crypto as Crypto & { randomUUID?: () => string };
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, ch => {
    const rand = Math.floor(Math.random() * 16);
    const value = ch === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};


export const setCookie = (key: string, value: string, expires: Date) => {
  console.log("helpers/Cookie.helper.tsx::setCookie", key, value, expires);
  if (!expires) {
    expires = new Date();
    expires.setDate(expires.getDate() + 1);
  }

  const finalValue = process.env.REACT_APP_ENV === 'local'
    ? value
    : btoa(encodeURIComponent(value));

  cookie.save(key, finalValue, {
    expires,
    path: "/",
    domain: window.location.hostname
  });
};

export const getCookie = (key: string) => {
  const value = cookie.load(key);
  if (!value) return null;

  if (process.env.REACT_APP_ENV === 'local') {
    return value;
  }

  try {
    return decodeURIComponent(atob(value));
  } catch (e) {
    console.error('쿠키 복호화 실패:', e);
    return null;
  }
};

export const removeAllPrefixCookies = () => {
  const prefix = getCookiePrefix();
  const allCookies = document.cookie.split(';');

  allCookies.forEach(cookieString => {
    const [key] = cookieString.split('=').map(part => part.trim());
    if (key.startsWith(prefix)) {
      removeCookie(key);
    }
  });
};

export const removeAllInstancePrefixCookies = () => {
  const prefix = getCookieInstancePrefix() + "+";
  const allCookies = document.cookie.split(';');

  allCookies.forEach(cookieString => {
    const [key] = cookieString.split('=').map(part => part.trim());
    if (key.startsWith(prefix)) {
      removeCookie(key);
    }
  });
};

export const removeCookie = (key: string) => {
  return cookie.remove(key, { path: "/", domain: window.location.hostname });
};

export const getCookiePrefix = () => {
  return COOKIE_PREFIX;
};

export const getCookieInstancePrefix = () => {
  return COOKIE_INSTANCE_PREFIX;
};

export const getIsRememberEmailKey = () => {
  return `${getCookiePrefix()}-is-remember-email`;
};

export const getRememberEmailKey = () => {
  return `${getCookiePrefix()}-remember-email`;
};

export const getLastLoginEmailKey = () => {
  return `${getCookiePrefix()}-last-login-email`;
};
export const getLastLoginEmail = () => {
  return getCookie(getLastLoginEmailKey());
};
export const setLastLoginEmail = (email: string) => {
  setCookie(getLastLoginEmailKey(), email, new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
};


export const getJwtKey = () => {
  return `${getCookieInstancePrefix()}-jwt`;
};
export const getJwt = () => {
  return getCookie(getJwtKey());
};

export const getJwtRefreshKey = () => {
  return `${getCookieInstancePrefix()}-jwt-refresh`;
};
export const getJwtRefresh = () => {
  return getCookie(getJwtRefreshKey());
};

export const getAgencyIdKey = () => {
  return `${getCookieInstancePrefix()}-agency-id`;
};
export const getAgencyId = () => {
  return getCookie(getAgencyIdKey());
};

export const getInstallAppBannerKey = () => {
  return `${getCookieInstancePrefix()}-install-app-banner`;
};
export const getInstallAppBanner = () => {
  return getCookie(getInstallAppBannerKey());
};

export const getPopupKey = (id: string) => {
  return `${getCookieInstancePrefix()}-popup-${id}`;
};
export const getPopup = (id: string) => {
  return getCookie(getPopupKey(id));
};

export const getCookieSettingKey = () => {
  return `${getCookiePrefix()}-cookie-setting`;
};
export const getCookieSetting = () => {
  const value = getCookie(getCookieSettingKey());
  console.log("helpers/Cookie.helper.tsx::getCookieSetting", value);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    console.error('쿠키 복호화 실패:', e);
    return value;
  }
};

export const setCookieSetting = (analytics: boolean) => {
  const obj = {
    analytics
  }
  setCookie(getCookieSettingKey(), JSON.stringify(obj), new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
}

export const getBrowserIdKey = () => {
  return `${getCookiePrefix()}-browser-id`;
};

export const getBrowserId = () => {
  return getCookie(getBrowserIdKey());
}

export const setBrowserId = () => {
  let browserId = getCookie(getBrowserIdKey());
  if (!browserId) {
    browserId = createUuidV4();
    setCookie(getBrowserIdKey(), browserId, new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
  }
  console.log("browserId", browserId);
}

export const setIsRememberEmail = (remember: boolean) => {
  console.log("helpers/Cookie.helper.tsx::setIsRememberEmail", remember);
  setCookie(getIsRememberEmailKey(), remember.toString(), new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
}

export const getIsRememberEmail = () => {
  console.log("helpers/Cookie.helper.tsx::getRememberEmail", getCookie(getIsRememberEmailKey()));
  return getCookie(getIsRememberEmailKey()) === "true";
}

export const removeIsRememberEmail = () => {
  console.log("helpers/Cookie.helper.tsx::removeRememberEmail", getIsRememberEmailKey());
  removeCookie(getIsRememberEmailKey());
}

export const setRememberEmail = (email: string) => {
  console.log("helpers/Cookie.helper.tsx::setRememberEmail", email);
  setCookie(getRememberEmailKey(), email, new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
}

export const getRememberEmail = () => {
  console.log("helpers/Cookie.helper.tsx::getRememberEmail", getCookie(getRememberEmailKey()));
  return getCookie(getRememberEmailKey());
}

export const removeRememberEmail = () => {
  console.log("helpers/Cookie.helper.tsx::removeRememberEmail", getRememberEmailKey());
  removeCookie(getRememberEmailKey());
}